import React, { useEffect, useState } from 'react';

import MyButton from '../../../../shared-components/button/Button';
import CustomInput from '../../../../shared-components/custom-input/CustomInput';
import ModalBox from '../../../../shared-components/modal-box/ModalBox';
import SelectField from '../../../../shared-components/select-field/SelectField';
import SuccessNotification from '../../../../shared-components/success-notification/SuccessNotification';
import deviceApi from '../../../../utils/api/device-hub';

const CURRENT_TYPE_OPTIONS = [
  { value: 'AC', label: 'AC' },
  { value: 'DC', label: 'DC' },
];

interface AddConnectorRatingDrawerProperties {
  /** Whether the modal is open */
  open: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** Existing connector rating option values to detect duplicates */
  existingValues: string[];
  /**
   * Called after a connector rating is successfully created.
   * Passes the newly created rating id back to the parent.
   * @param ratingId - Id of the created rating
   */
  onRatingCreated: (ratingId?: string) => void;
}

/**
 * Slide-in drawer modal for adding a new connector rating.
 * @param {AddConnectorRatingDrawerProperties} properties - The component properties.
 * @param {boolean} properties.open - Whether the modal is open.
 * @param {Function} properties.onClose - Callback to close the modal.
 * @param {string[]} properties.existingValues - Existing connector rating option values to detect duplicates.
 * @param {Function} properties.onRatingCreated - Callback called after a connector rating is successfully created.
 * @returns {React.ReactElement} The rendered modal component.
 */
function AddConnectorRatingDrawer({
  open,
  onClose,
  existingValues,
  onRatingCreated,
}: Readonly<AddConnectorRatingDrawerProperties>) {
  const [ratingName, setRatingName] = useState('');
  const [currentType, setCurrentType] = useState('AC');
  const [maxPower, setMaxPower] = useState('');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showFormErrors, setShowFormErrors] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isRatingNameEdited, setIsRatingNameEdited] = useState(false);

  /**
   * Resets all form fields to their initial state.
   */
  const resetForm = () => {
    setRatingName('');
    setCurrentType('AC');
    setMaxPower('');
    setFormError('');
    setShowFormErrors(false);
    setIsRatingNameEdited(false);
  };

  /**
   * Handles closing the modal and resetting the form.
   */
  const handleClose = () => {
    resetForm();
    onClose();
  };

  /**
   * Validates and submits the connector rating creation form.
   */
  const handleSubmit = () => {
    setShowFormErrors(true);
    if (!ratingName.trim()) {
      return;
    }
    if (!maxPower.trim()) {
      return;
    }
    const numericPart = maxPower.trim().replace(/[^0-9.]/g, '');
    if (!numericPart) {
      setFormError('Please enter a valid numeric value for Max Power Output');
      return;
    }

    const rawNumericPart = Math.floor(Number(numericPart)).toString();
    const value = `${rawNumericPart}kw`;
    if (existingValues.includes(value)) {
      setFormError(`A connector rating for ${rawNumericPart}kw exists`);
      return;
    }

    setFormError('');
    setSubmitting(true);

    const payload = {
      name: ratingName.trim(),
      currentType: currentType.toUpperCase(),
      capacityKW: numericPart,
      unit: 'kW',
      isActive: true,
    };

    deviceApi
      .createConnecterRating(payload)
      .then((response: any) => {
        const newRating = response?.data?.results?.[0] || response?.data || {};
        const ratingId = newRating._id || newRating.id || '';
        onRatingCreated(ratingId);
        setShowSuccess(true);
      })
      .catch((error: any) => {
        setFormError(
          error?.response?.data?.message || error?.message || 'Failed to create connector rating',
        );
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  useEffect(() => {
    if(!isRatingNameEdited){
      if(maxPower){
        setRatingName(`${currentType} ${maxPower} kw`);
      }else{
        setRatingName('')
      }
    }
  }, [currentType, maxPower, isRatingNameEdited]);

  return (
    <>
      <ModalBox
        open={open}
        title=""
        content={
          <div className="left-drawer-content rating-drawer">
            <div className="drawer-header">
              <h3 className="drawer-title">Add New Connector Ratings</h3>
            </div>
            <div className="drawer-body">
              <div className="drawer-grid-2">
                <SelectField
                  name="ratingCurrentType"
                  inputLabel="Current Type"
                  options={CURRENT_TYPE_OPTIONS}
                  placeholder="Choose Current Type"
                  selectedValue={currentType}
                  onChangeValue={(_, value) => {
                    setCurrentType(value.toString());
                    if (formError) setFormError('');
                  }}
                  error={showFormErrors && !currentType}
                  errorMessage="Current Type is required"
                  valueRequired
                />
                <CustomInput
                  type="number"
                  name="ratingMaxPowerInput"
                  inputLabel="Max Power Output"
                  placeholder="Enter power (eg.50.203)"
                  value={maxPower}
                  onChangeValue={(_, value) => {
                    const input = value.toString();
                    if (!/^\d*\.?\d*$/.test(input)) {
                      return;
                    }

                    const number = Number(input);
                    if (number < 10) {
                      if (!/^\d*\.?\d{0,1}$/.test(input)) {
                        return;
                      }
                    }

                    if (number >= 10) {
                      if (!/^\d*\.?\d{0,3}$/.test(input)) {
                        return;
                      }
                    }

                    setMaxPower(input);
                    if (formError) {
                      setFormError('');
                    }
                  }}
                  svgIcon={
                    <span
                      className="suffix"
                      style={{ fontSize: '11px', color: '#9aa3b2', fontWeight: 600 }}>
                      kW
                    </span>
                  }
                  error={showFormErrors && !maxPower.trim()}
                  errorMessage="Max Power Output is required"
                  valueRequired
                  maxValue={1000}
                  decimalLimit={3}
                />
              </div>
              <CustomInput
                type="text"
                name="ratingNameInput"
                inputLabel="Rating Name"
                placeholder="Enter rating name (eg.AC Connector 3.3kW)"
                value={ratingName}
                onChangeValue={(_, value) => {
                  setRatingName(value.toString());
                  setIsRatingNameEdited(true)
                  if (formError) setFormError('');
                }}
                error={showFormErrors && !ratingName.trim()}
                errorMessage="Rating Name is required"
                valueRequired
                characterLimit={100}
              />
              {formError && <div className="drawer-error">{formError}</div>}
            </div>
            <div className="footer-wrap">
              <div className="button-wrap">
                <MyButton label="Cancel" buttonType="error" onClickFunc={handleClose} />
              </div>
              <div className="button-wrap">
                <MyButton
                  label="Save"
                  buttonType="submit"
                  onClickFunc={handleSubmit}
                  disableButton={submitting}
                  loading={submitting}
                />
              </div>
            </div>
          </div>
        }
        closeModal={handleClose}
        header={false}
        position="right"
        modalBoxAnimation="modalAnimateFromRight"
      />
      <ModalBox
        open={showSuccess}
        title=""
        content={
          <SuccessNotification
            title="Connector Rating added Successfully"
            description="The connector rating has been added successfully."
            onNotificationClose={() => {
              setShowSuccess(false);
              resetForm();
              onClose();
            }}
          />
        }
        closeModal={() => {
          setShowSuccess(false);
          resetForm();
          onClose();
        }}
        header={false}
        notification
      />
    </>
  );
}

export default AddConnectorRatingDrawer;
